import { useTranslation } from "react-i18next"

import { Eye } from "@/components/icons"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { useConfigQuery } from "@/hooks/use-config-query"
import { useAdminCustomerPreviewStore } from "@/stores/admin-customer-preview-store"

/**
 * Toggle for the per-browser "preview as customer admin" mode on the
 * Management tab, modeled on `TenderAdminModeToggle`. Rendered only for
 * platform admins. Unlike tender admin mode this is view-only — it doesn't
 * ride along on requests, since no backend endpoint enforces
 * `require_admin_capability` yet; flipping it only changes what the current
 * platform admin sees rendered, filtered against the live delegation matrix.
 */
export function CustomerPreviewToggle({
  onRequestChange,
}: {
  onRequestChange?: (next: boolean) => void
}) {
  const { t } = useTranslation()
  const { data: config } = useConfigQuery()
  const isPlatformAdmin = config?.user_policy.is_platform_admin ?? false
  const enabled = useAdminCustomerPreviewStore((s) => s.enabled)
  const setEnabled = useAdminCustomerPreviewStore((s) => s.setEnabled)
  const label = t("adminDashboard.management.customerPreview.toggleLabel", {
    defaultValue: "Preview management settings as Customer Admin",
  })

  if (!isPlatformAdmin) return null

  return (
    <div className="flex items-center gap-2">
      <Eye className="text-muted-foreground size-3.5" />
      <Label className="cursor-pointer text-xs font-medium" htmlFor="admin-customer-preview">
        {label}
      </Label>
      <Switch
        aria-label={label}
        checked={enabled}
        id="admin-customer-preview"
        onCheckedChange={(next) => {
          if (onRequestChange) onRequestChange(next)
          else setEnabled(next)
        }}
      />
    </div>
  )
}
